'use client';

/**
 * Injects the Google Tag Manager container (GitLab #649, AC3/AC7/AC8).
 *
 * GTM is the only script this application delivers, and it is delivered
 * once and only once: when analytics is switched on, a container ID is
 * configured, and the customer has granted at least one optional category.
 * Until all three hold, nothing is requested — not the container, not a
 * DNS lookup for `googletagmanager.com`.
 *
 * That is stricter than Consent Mode alone requires. Consent Mode would let
 * the container load and then behave itself, but a container present on the
 * page is a third-party request the customer has not agreed to, and the ICO
 * treats the request itself as the thing needing consent.
 *
 * There is deliberately **no `<noscript>` iframe**. The standard GTM snippet
 * includes one, and it is the single most common way a consent
 * implementation leaks: with JavaScript disabled there is no gate, no
 * stored choice and no way to ask — so the iframe would fire GTM for
 * exactly the visitors who could never consent to it.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import Script from 'next/script';
import { pushGtmStart } from '@/lib/analytics/dataLayer';
import { useConsent } from './ConsentProvider';
interface GtmLoaderProps {
  /** Container ID, `GTM-XXXXXXX`. Comes from the org's analytics settings. */
  containerId?: string;
  /**
   * Per-request CSP nonce. GTM reads the nonce from its own script element
   * and stamps it onto every tag it injects, so without this the container
   * loads and then every tag inside it is blocked by the CSP — a failure
   * that looks like "analytics is broken" rather than "the nonce is missing".
   */
  nonce?: string;
  /** Fired once the container has loaded; used by the Phase 8 e2e checks. */
  onLoad?: () => void;
}

/** GTM container IDs are `GTM-` followed by an alphanumeric suffix. */
const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,10}$/;
export function GtmLoader(t0) {
  const $ = _c(10);
  const {
    containerId,
    nonce,
    onLoad
  } = t0;
  const {
    shouldLoadGtm
  } = useConsent();
  let t1;
  if ($[0] !== containerId || $[1] !== shouldLoadGtm) {
    t1 = shouldLoadGtm && !!containerId && CONTAINER_ID_PATTERN.test(containerId);
    $[0] = containerId;
    $[1] = shouldLoadGtm;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const canLoad = t1;
  let t2;
  let t3;
  if ($[3] !== canLoad) {
    t2 = () => {
      if (canLoad) {
        pushGtmStart();
      }
    };
    t3 = [canLoad];
    $[3] = canLoad;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!canLoad || !containerId) {
    return null;
  }
  const t4 = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  let t5;
  if ($[6] !== nonce || $[7] !== onLoad || $[8] !== t4) {
    t5 = <Script id="gtm-container" strategy="afterInteractive" nonce={nonce} src={t4} onLoad={onLoad} />;
    $[6] = nonce;
    $[7] = onLoad;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}